import React, { useState, useEffect } from "react";
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker, message,} from "antd";
import { errHandler } from "../../helper/helper";
import { getMonitorCustomerDetailsReport } from "./GeneralService";
import generateMonitorCustomerDetailsExcel from "../../Utils/MonitorCustomerDetailsExcel";

const { Option } = Select;

const REQUEST_TYPE_OPTIONS = [
    { label: "All",          value: "ALL"          },
    { label: "NIC",          value: "NIC"          },
];

const STATUS_OPTIONS = [
    { label: "All",      value: "ALL" },
    { label: "Approved", value: "APPROVED" },
    { label: "Pending",  value: "PENDING" },
    { label: "Rejected", value: "REJECTED" },
];

const MonitorCustomerDetailsReport = ({
    loginPlaceId,
    districtData,
    districtName,
    divisionalId,
    branchId,
}) => {
    const [selectedDistrict, setSelectedDistrict]       = useState(null);
    const [selectedDivisional, setSelectedDivisional]   = useState([]);
    const [selectedBranch, setSelectedBranch]           = useState([]);
    const [selectedRequestType, setSelectedRequestType] = useState("NIC");
    const [selectedStatus, setSelectedStatus]           = useState("ALL");
    const [fromDate, setFromDate]                       = useState(null);
    const [toDate, setToDate]                           = useState(null);
    const [loading, setLoading] = useState(false);

    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches]               = useState([]);

    const [initialDivisionalId] = useState(divisionalId || null);
    const [initialDistrictName] = useState(districtName || null);

    // Resolve branch name for placeId "1" (branch-level user)
    const fixedBranchName =
        loginPlaceId === "1" && branchId
            ? (() => {
                  for (const d of districtData || []) {
                      for (const div of d.divisionalAreaDtoList || []) {
                          const b = (div.branchDtoList || []).find((b) => b.id === branchId);
                          if (b) return b.branchName;
                      }
                  }
                  return "Branch";
              })()
            : null;

    useEffect(() => {
        if (loginPlaceId === "1" && districtData) {
            const branches = [];
            for (const d of districtData || []) {
                for (const div of d.divisionalAreaDtoList || []) {
                    for (const b of div.branchDtoList || []) {
                        branches.push(b);
                    }
                }
            }
            setAvailableBranches(branches);
            if (branchId && (!selectedBranch || selectedBranch.length === 0)) {
                setSelectedBranch([branchId]);
            }
        }
    }, [districtData, loginPlaceId, branchId]);

    useEffect(() => {
        if (loginPlaceId === "1") return;
        if (initialDistrictName && loginPlaceId === "4") {
            const district = (districtData || []).find(
                (d) => d.districtName === initialDistrictName
            );
            if (district) setSelectedDistrict(district.districtId);
        }
    }, [initialDistrictName, loginPlaceId, districtData]);

    useEffect(() => {
        if (loginPlaceId === "1") return;
        let filtered = [];
        if (loginPlaceId === "2" && initialDivisionalId) {
            filtered = (districtData || [])
                .flatMap((d) => d.divisionalAreaDtoList || [])
                .filter((div) => div.divAId === initialDivisionalId);
            setSelectedDivisional([initialDivisionalId]);
        } else {
            filtered = (districtData || [])
                .filter((d) => d.districtId === selectedDistrict)
                .flatMap((d) => d.divisionalAreaDtoList || []);
        }
        setAvailableDivisionalAreas(filtered);
        
        setSelectedDivisional((prev) => 
            (prev || []).filter((id) => filtered.some((d) => d.divAId === id))
        );
        setSelectedBranch([]);
        setAvailableBranches([]);
    }, [selectedDistrict, districtData, loginPlaceId, initialDivisionalId]);

    useEffect(() => {
        if (loginPlaceId === "1") return;
        if (!selectedDivisional || selectedDivisional.length === 0) {
            setAvailableBranches([]);
            setSelectedBranch([]);
            return;
        }
        const divAreas = availableDivisionalAreas.filter((d) =>
            selectedDivisional.includes(d.divAId)
        );
        const branches = divAreas.flatMap((d) => d.branchDtoList || []);
        
        setAvailableBranches(branches);
        
        setSelectedBranch((prev) => 
            (prev || []).filter((id) => branches.some((b) => b.id === id))
        );
    }, [selectedDivisional, availableDivisionalAreas, loginPlaceId]);

    const isGenerateDisabled = !(fromDate && toDate);

    const handleDivisionalChange = (values) => {
        if (values.includes("ALL")) {
            setSelectedDivisional(availableDivisionalAreas.map((d) => d.divAId));
        } else {
            setSelectedDivisional(values);
        }
        setSelectedBranch([]);
    };

    const handleBranchChange = (values) => {
        if (values.includes("ALL")) {
            setSelectedBranch(availableBranches.map((b) => b.id));
        } else {
            setSelectedBranch(values);
        }
    };

    const generateReport = async () => {
        if (!fromDate || !toDate) {
            message.warning("Please select both From Date and To Date.");
            return;
        }
        setLoading(true);
        try {
            const fromDateISO = fromDate.startOf("day").toDate().toISOString();
            const toDateISO   = toDate.endOf("day").toDate().toISOString();

            const allBranchesSelected =
                availableBranches.length > 0 &&
                selectedBranch.length === availableBranches.length;

            const branchLabel = (() => {
                if (allBranchesSelected) return "All";
                if (selectedBranch && selectedBranch.length > 0) {
                    return selectedBranch
                        .map((id) => {
                            const b = availableBranches.find((b) => b.id === id);
                            return b ? b.branchName : id;
                        })
                        .join(", ");
                }
                if (loginPlaceId === "1") return fixedBranchName || "Branch";
                return "All";
            })();

            const effectiveBranchList = (() => {
                if (selectedBranch && selectedBranch.length > 0 && !allBranchesSelected) {
                    return selectedBranch;
                }
                if (availableBranches.length > 0) {
                    return availableBranches.map((b) => b.id);
                }
                if (loginPlaceId === "1" && branchId) {
                    return [branchId];
                }
                return (districtData || [])
                    .flatMap((d) => d.divisionalAreaDtoList || [])
                    .flatMap((div) => div.branchDtoList || [])
                    .map((b) => b.id);
            })();

            const payload = {
                fromDate:     fromDateISO,
                toDate:       toDateISO,
                changeType:   selectedRequestType !== "ALL" ? selectedRequestType : null,
                requestType:  selectedRequestType !== "ALL" ? selectedRequestType : null,
                status:       selectedStatus !== "ALL" ? selectedStatus : "ALL",
                branchId:
                    effectiveBranchList && effectiveBranchList.length > 0
                        ? effectiveBranchList[0]
                        : null,
                branchIdList: effectiveBranchList,
            };

            const response = await getMonitorCustomerDetailsReport(payload);
            const rawData  = Array.isArray(response) ? response : [];

            if (rawData.length === 0) {
                message.warning("No data found for the selected parameters.");
                return;
            }

            const filters = {
                fromDate:    fromDate.format("YYYY-MM-DD"),
                toDate:      toDate.format("YYYY-MM-DD"),
                requestType: selectedRequestType || "All",
                status:      selectedStatus      || "All",
                branch:      branchLabel,
            };
            await generateMonitorCustomerDetailsExcel(rawData, filters);
            message.success(`Report generated successfully (${rawData.length} records).`);
        } catch (error) {
            console.error("MonitorCustomerDetailsReport error:", error);
            errHandler(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]} justify="space-around">

                        {/* District – placeId 3 & 4 */}
                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="District">
                                    <Select
                                        value={selectedDistrict}
                                        onChange={setSelectedDistrict}
                                        disabled={loginPlaceId === "4"}
                                        allowClear showSearch
                                        placeholder="Select District"
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {(districtData || []).map((d) => (
                                            <Option key={d.districtId} value={d.districtId}>
                                                {d.districtName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Divisional Area – placeId 2, 3, 4 */}
                        {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Divisional Area">
                                    <Select
                                        mode="multiple"
                                        value={selectedDivisional}
                                    onChange={handleDivisionalChange}
                                    disabled={loginPlaceId === "2"}
                                    allowClear showSearch
                                    placeholder="Select Divisional"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    <Option key="ALL" value="ALL">
                                        Select All Divisional Areas
                                    </Option>
                                    {availableDivisionalAreas.map((d) => (
                                            <Option key={d.divAId} value={d.divAId}>
                                                {d.divAIdName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Branch – all placeIds */}
                        <Col span={24}>
                            <Form.Item label="Branch">
                                <Select
                                    mode="multiple"
                                    value={selectedBranch}
                                    onChange={handleBranchChange}
                                    allowClear showSearch
                                    placeholder="Select Branch"
                                    disabled={!availableBranches.length || loginPlaceId === "1"}
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    <Option key="ALL" value="ALL">
                                        Select All Branches
                                    </Option>
                                    {availableBranches.map((b) => (
                                        <Option key={b.id} value={b.id}>
                                            {b.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Change Type – maps to request_type in DB */}
                        <Col span={24}>
                            <Form.Item label="Change Type">
                                <Select
                                    value={selectedRequestType}
                                    onChange={setSelectedRequestType}
                                    allowClear={false}
                                    style={{ width: "100%" }}
                                >
                                    {REQUEST_TYPE_OPTIONS.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Status */}
                        <Col span={24}>
                            <Form.Item label="Status">
                                <Select
                                    value={selectedStatus}
                                    onChange={setSelectedStatus}
                                    allowClear={false}
                                    style={{ width: "100%" }}
                                >
                                    {STATUS_OPTIONS.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Date range */}
                        <Col span={24}>
                            <Form.Item label="From Date">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    value={fromDate}
                                    onChange={(date) => setFromDate(date)}
                                    format="YYYY-MM-DD"
                                    placeholder="Select start date"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="To Date">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    value={toDate}
                                    onChange={(date) => setToDate(date)}
                                    format="YYYY-MM-DD"
                                    placeholder="Select end date"
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col span={24} style={{ textAlign: 'right' }}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    className="generate-btn"
                                    onClick={generateReport}
                                    disabled={loading || isGenerateDisabled}
                                >
                                    {loading ? <Spin /> : "Generate Report"}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default MonitorCustomerDetailsReport;
